/**
 * useTenantQuery - Hook factory for tenant-scoped API queries
 *
 * Eliminates auth/tenant boilerplate from React Query hooks by:
 * - Automatically injecting auth token via API client interceptors
 * - Automatically injecting tenant ID via API client interceptors
 * - Providing consistent error handling with parseApiError()
 * - Managing enabled state based on authentication
 * - Supporting query parameters and custom options
 *
 * @example
 * // Before (80+ lines per hook):
 * export function useWeatherAlerts(filters?: WeatherAlertFilters) {
 *   const { token } = useAuthStore();
 *   const { activeTenant } = useTenantStore();
 *
 *   return useQuery<WeatherAlertsResponse>({
 *     queryKey: ['weather-alerts', activeTenant?.id, filters],
 *     queryFn: async () => {
 *       const params = new URLSearchParams();
 *       if (filters?.severity) params.append('severity', filters.severity);
 *       // ... 10 more lines
 *       const response = await fetch(`${API_URL}/api/weather/alerts?${params}`, {
 *         headers: {
 *           Authorization: `Bearer ${token}`,
 *           'X-Tenant-ID': activeTenant?.id || '',
 *         },
 *       });
 *       if (!response.ok) throw new Error('Failed to fetch');
 *       return response.json();
 *     },
 *     enabled: !!token && !!activeTenant,
 *     staleTime: 60000,
 *   });
 * }
 *
 * // After (5 lines with useTenantQuery):
 * export const useWeatherAlerts = (filters?: WeatherAlertFilters) =>
 *   useTenantQuery<WeatherAlertsResponse>(
 *     ['weather-alerts', filters],
 *     '/weather/alerts',
 *     { params: filters, staleTime: 60000 }
 *   );
 */

import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
  QueryKey,
  InvalidateQueryFilters,
  RefetchQueryFilters,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { api } from '@/services/api';
import { parseApiError } from '@/utils/api-errors';

export interface UseTenantQueryOptions<TData = unknown, TSelect = TData> {
  /** Query parameters to append to the URL */
  params?: Record<string, any>;
  /** Custom stale time in milliseconds (default: 30000 = 30 seconds) */
  staleTime?: number;
  /** Custom enabled condition (in addition to auth/tenant checks) */
  enabled?: boolean;
  /** Custom timeout in milliseconds (overrides default 30s from API client) */
  timeout?: number;
  /** Additional React Query options (select may transform TData to TSelect) */
  queryOptions?: Omit<UseQueryOptions<TData, Error, TSelect, QueryKey>, 'queryKey' | 'queryFn' | 'enabled' | 'staleTime'>;
}

/**
 * Hook factory for tenant-scoped GET requests
 *
 * @param key - React Query key (will be combined with tenant ID automatically)
 * @param endpoint - API endpoint (e.g., '/weather/alerts')
 * @param options - Optional configuration (params, staleTime, enabled, etc.)
 * @returns React Query result with automatic auth/tenant injection
 *
 * @example
 * // Simple query with no parameters
 * const { data, isLoading, error } = useTenantQuery<Incident[]>(
 *   ['incidents'],
 *   '/incidents'
 * );
 *
 * @example
 * // Query with filters and custom stale time
 * const { data } = useTenantQuery<Task[]>(
 *   ['tasks', filters],
 *   '/tasks',
 *   {
 *     params: filters,
 *     staleTime: 60000,
 *   }
 * );
 *
 * @example
 * // Query with custom enabled condition
 * const { data } = useTenantQuery<SearchResults>(
 *   ['search', query],
 *   '/search',
 *   {
 *     params: { q: query },
 *     enabled: query.length >= 2, // Only query if search term is 2+ chars
 *   }
 * );
 */
export function useTenantQuery<TData = unknown, TSelect = TData>(
  key: QueryKey,
  endpoint: string,
  options: UseTenantQueryOptions<TData, TSelect> = {}
): ReturnType<typeof useQuery<TData, Error, TSelect>> {
  const { token } = useAuthStore();
  const { activeTenant } = useTenantStore();

  const {
    params,
    staleTime = 30000,
    enabled: customEnabled = true,
    timeout,
    queryOptions = {},
  } = options;

  // Combine base enabled condition (auth + tenant) with custom condition
  const isEnabled = !!token && !!activeTenant && customEnabled;

  // Combine tenant ID with the provided query key
  const queryKey: QueryKey = Array.isArray(key)
    ? [activeTenant?.id, ...key]
    : [activeTenant?.id, key];

  return useQuery<TData, Error, TSelect>({
    queryKey,
    queryFn: async (): Promise<TData> => {
      try {
        // Build query string from params
        let url = endpoint;
        if (params) {
          const searchParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              // Handle arrays
              if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(key, String(v)));
              } else {
                searchParams.append(key, String(value));
              }
            }
          });
          const queryString = searchParams.toString();
          if (queryString) {
            url += `?${queryString}`;
          }
        }

        // Use the API client which automatically adds auth headers
        // Note: timeout is handled by the axios instance (30s default)
        // Custom timeout would require creating a new axios instance or using CancelToken
        return await api.get<TData>(url);
      } catch (error) {
        // Parse the error into a consistent format, but PRESERVE the HTTP
        // status so the queryClient retry policy and downstream consumers
        // (e.g. useActiveIncidentSync) can distinguish transient failures
        // like 429 from deterministic ones like 404.
        const apiError = parseApiError(error);
        const wrapped = new Error(apiError.message) as Error & {
          status?: number;
          code?: string;
        };
        if (apiError.status !== undefined) wrapped.status = apiError.status;
        if (apiError.code !== undefined) wrapped.code = apiError.code;
        throw wrapped;
      }
    },
    enabled: isEnabled,
    staleTime,
    ...queryOptions,
  });
}

/**
 * Build a URLSearchParams object from a filter object
 *
 * Utility function for building query strings manually when needed.
 * useTenantQuery handles this automatically via the `params` option.
 *
 * @param params - Object with query parameters
 * @returns URLSearchParams instance
 *
 * @example
 * const params = buildQueryParams({ status: 'active', priority: ['high', 'critical'] });
 * // URLSearchParams { 'status' => 'active', 'priority' => 'high', 'priority' => 'critical' }
 */
export function buildQueryParams(params: Record<string, any>): URLSearchParams {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  return searchParams;
}

/**
 * useTenantQueryKey - Build a tenant-prefixed query key
 *
 * `useTenantQuery` stores cached data under `[activeTenant?.id, ...key]`. Any
 * code that needs to reference that cache entry directly (e.g. a manual
 * `queryClient.invalidateQueries` / `refetchQueries` / `getQueryData`) MUST use
 * the same prefixed shape or React Query's prefix-matching will silently fail
 * to match — the server mutation succeeds but the UI stays stale until reload.
 *
 * @returns A stable function that prepends the active tenant id to a query key.
 *
 * @example
 * const tenantKey = useTenantQueryKey();
 * queryClient.invalidateQueries({ queryKey: tenantKey(['shelters']) });
 * // → invalidates [activeTenantId, 'shelters'] (matches useTenantQuery(['shelters'], ...))
 */
export function useTenantQueryKey(): (key: QueryKey) => QueryKey {
  const { activeTenant } = useTenantStore();
  const tenantId = activeTenant?.id;

  return useCallback(
    (key: QueryKey): QueryKey => {
      const keyArray = Array.isArray(key) ? key : [key];
      return tenantId ? [tenantId, ...keyArray] : [...keyArray];
    },
    [tenantId]
  );
}

/**
 * useInvalidateTenantQueries - Invalidate/refetch tenant-scoped query caches safely
 *
 * Wraps `queryClient.invalidateQueries` / `refetchQueries`, auto-prefixing the
 * active tenant id onto the `queryKey` so it matches the `[tenantId, ...key]`
 * shape that `useTenantQuery` writes under. Use this instead of calling
 * `queryClient.invalidateQueries({ queryKey: ['shelters'] })` directly whenever
 * the data you want to refresh was read via `useTenantQuery` — passing a bare
 * `['shelters']` key would NOT prefix-match `[tenantId, 'shelters', ...]` and
 * the invalidation would silently no-op.
 *
 * Only the `queryKey` is rewritten; all other filter options (exact, predicate,
 * refetchType, etc.) and the second `options` arg pass through untouched.
 *
 * @example
 * const invalidateTenant = useInvalidateTenantQueries();
 * // in a mutation's onSuccess:
 * invalidateTenant.invalidate(['shelters']);
 * invalidateTenant.invalidate(['shelters', shelterId]);
 * await invalidateTenant.refetch(['dashboard-stats']);
 */
export function useInvalidateTenantQueries() {
  const queryClient = useQueryClient();
  const toTenantKey = useTenantQueryKey();

  const invalidate = useCallback(
    (
      key: QueryKey,
      filters?: Omit<InvalidateQueryFilters, 'queryKey'>,
      options?: Parameters<typeof queryClient.invalidateQueries>[1]
    ) =>
      queryClient.invalidateQueries(
        { ...filters, queryKey: toTenantKey(key) },
        options
      ),
    [queryClient, toTenantKey]
  );

  const refetch = useCallback(
    (
      key: QueryKey,
      filters?: Omit<RefetchQueryFilters, 'queryKey'>,
      options?: Parameters<typeof queryClient.refetchQueries>[1]
    ) =>
      queryClient.refetchQueries(
        { ...filters, queryKey: toTenantKey(key) },
        options
      ),
    [queryClient, toTenantKey]
  );

  return { invalidate, refetch };
}
